/**
 * CorporateLoginPage - Corporate user login
 * Supports email/password and SSO (Google, Microsoft)
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CircularProgress,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { loginCorporate, clearError } from '../../redux/corporateSlice';
import GoogleIcon from '@mui/icons-material/Google';
import BusinessIcon from '@mui/icons-material/Business';
import ErrorAlert from '../../components/Auth/ErrorAlert';
import RateLimiter from '../../core/auth/RateLimiter';
import AuditLogger from '../../core/auth/AuditLogger';
import PublicHeader from '../../components/Layout/PublicHeader';

export const CorporateLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.corporate);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [localError, setLocalError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setLocalError('Please enter both email and password');
      return;
    }

    // Check rate limiting
    if (!RateLimiter.isAllowed('login')) {
      const errorMsg = RateLimiter.getErrorMessage('login');
      setRateLimitError(errorMsg);
      return;
    }

    setLocalError(null);
    setRateLimitError(null);

    try {
      const result = await dispatch(loginCorporate(formData));
      if (loginCorporate.fulfilled.match(result)) {
        await AuditLogger.logLogin(formData.email, true);
        navigate('/corporate-dashboard');
      } else {
        const errorMsg = result.payload as string;
        setLocalError(errorMsg || 'Login failed');
        await AuditLogger.logLogin(formData.email, false, errorMsg);
      }
    } catch (err: any) {
      setLocalError(err.message || 'Login failed. Please try again.');
      await AuditLogger.logLogin(formData.email, false, err.message);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Check rate limiting
      if (!RateLimiter.isAllowed('login')) {
        const errorMsg = RateLimiter.getErrorMessage('login');
        setRateLimitError(errorMsg);
        return;
      }

      setLocalError(null);
      setRateLimitError(null);

      // TODO: Implement Google OAuth flow
      // 1. Initialize Google Sign-In with google-auth-library
      // 2. Get ID token from Google
      // 3. Dispatch loginWithGoogle action
      // 4. Redirect to dashboard on success
      
      console.warn('Google OAuth not yet implemented. Install @react-oauth/google package.');
      setLocalError('Google login is not yet configured. Please use email/password login.');
    } catch (err: any) {
      setLocalError(err.message || 'Google login failed');
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      // Check rate limiting
      if (!RateLimiter.isAllowed('login')) {
        const errorMsg = RateLimiter.getErrorMessage('login');
        setRateLimitError(errorMsg);
        return;
      }

      setLocalError(null);
      setRateLimitError(null);

      // TODO: Implement Microsoft OAuth flow
      // 1. Initialize Microsoft Sign-In with @azure/msal-browser
      // 2. Get ID token from Microsoft
      // 3. Dispatch loginWithMicrosoft action
      // 4. Redirect to dashboard on success
      
      console.warn('Microsoft OAuth not yet implemented. Install @azure/msal-browser package.');
      setLocalError('Microsoft login is not yet configured. Please use email/password login.');
    } catch (err: any) {
      setLocalError(err.message || 'Microsoft login failed');
    }
  };

  return (
    <>
      <Helmet>
        <title>Corporate Login — HUSLRZ</title>
      </Helmet>
      <PublicHeader activeLink="login" />
      <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{
          minHeight: 'calc(100vh - 60px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4
        }}>
        <Card sx={{
          width: '100%',
          p: { xs: 3, sm: 4 },
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2
            }}>
              <BusinessIcon sx={{
                fontSize: 48,
                color: '#14B8A6'
              }} />
            </Box>
            <Typography variant="h4" sx={{
              fontWeight: 'bold',
              color: '#0D3D3A',
              mb: 1
            }}>
              HUSLRZ Corporate
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Employee Wellness Platform
            </Typography>
          </Box>

          {/* Error Alert */}
          {(error || localError || rateLimitError) && (
            <ErrorAlert
              message={error || localError || rateLimitError || ''}
              severity={rateLimitError ? 'warning' : 'error'}
              onClose={() => {
                dispatch(clearError());
                setLocalError(null);
                setRateLimitError(null);
              }}
            />
          )}

          {/* Email/Password Form */}
          <Box component="form" onSubmit={handleLogin} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
              margin="normal"
              variant="outlined"
              placeholder="your.email@company.com"
              required
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
              margin="normal"
              variant="outlined"
              placeholder="••••••••"
              required
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading || !formData.email || !formData.password}
              sx={{
                mt: 3,
                py: 1.5,
                backgroundColor: '#14B8A6',
                '&:hover': {
                  backgroundColor: '#0F766E'
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Login'
              )}
            </Button>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 3 }}>OR</Divider>

          {/* SSO Options */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderColor: '#ddd',
                color: '#333',
                '&:hover': {
                  borderColor: '#14B8A6',
                  backgroundColor: '#f0fffe'
                }
              }}
            >
              Sign in with Google
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleMicrosoftLogin}
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderColor: '#ddd',
                color: '#333',
                '&:hover': {
                  borderColor: '#14B8A6',
                  backgroundColor: '#f0fffe'
                }
              }}
            >
              Sign in with Microsoft
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{
            mt: 4,
            pt: 3,
            borderTop: '1px solid #eee',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Are you a gym partner?
            </Typography>
            <Button
              color="primary"
              onClick={() => navigate('/login')}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                color: '#14B8A6',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              Go to Partner Login
            </Button>
          </Box>
        </Card>
      </Box>
    </Container>
    </>
  );
};

export default CorporateLoginPage;
